import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { getFileStackUrl } from 'api/Filestack';

import TiffViewer from 'components/shared/Document/components/TiffViewer';
import Icon from 'components/shared/Icon/Icon';
import Loading from 'components/shared/Loading/Loading';
import Modal from 'components/shared/Modal/Modal';

const PreviewContent = ({ type, handle, fileBlob }) => {
  const navigate = useNavigate();
  const [blobUrl, setBlobUrl] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['preview', handle],
    queryFn: () => getFileStackUrl(navigate, { handle }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!handle && !fileBlob
  });

  useEffect(() => {
    if (fileBlob) {
      const url = URL.createObjectURL(fileBlob);
      setBlobUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [fileBlob]);

  if (isLoading && !fileBlob) return <Loading />;

  const url = fileBlob ? blobUrl : data?.file;

  if (!url)
    return (
      <div className="flex flex-col items-center">
        <Icon icon="illustration-empty-data" />
        <h1 className="text-base font-500 text-primary-900">No attachment available</h1>
      </div>
    );

  const isTiff = type === 'image/tiff' || type === 'image/tif' || type === 'image/x-tiff';

  if (isTiff) {
    return (
      <div className="flex w-full justify-center">
        <TiffViewer src={url} maxHeight="80vh" />
      </div>
    );
  }

  if (type.startsWith('image/')) {
    return <img src={url} alt="Preview" className="max-h-[80vh] max-w-full" />;
  }

  if (type === 'application/pdf') {
    return <iframe src={url} title="PDF Preview" className="h-[85vh] w-full border-0" />;
  }

  return (
    <div className="flex h-full flex-col items-center justify-center">
      <Icon icon="file-alt" className="text-neutral-500 mb-4 text-4xl" />
      <p className="text-neutral-600">Preview is not available for this file type.</p>
      {!fileBlob && (
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
          Download File
        </a>
      )}
    </div>
  );
};

const PreviewFile = ({ isOpen, onClose, name, handle, type, fileBlob }) => {
  if (!isOpen || (!handle && !fileBlob)) return null;

  return (
    <Modal title={name} isOpen={isOpen} handleClose={onClose} width="medium">
      <div className="flex flex-1 items-center justify-center overflow-hidden p-4">
        <PreviewContent type={type} handle={handle} fileBlob={fileBlob} />
      </div>
    </Modal>
  );
};

export default PreviewFile;
